<template>
  <v-app id="inspire">
    <v-card-title>
      Shooting Program Template Data
      <v-spacer></v-spacer>
      <v-btn @click="changePathAddShootingProgramTemplate()" color="secondary">
        Add New
      </v-btn>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        prepend-icon="mdi-magnify"
        label="Search"
        single-line
        hide-details
        clearable
      ></v-text-field>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :search="search"
      :items="getShootingProgramTemplateSummary"
      :expanded.sync="expanded"
      :hide-default-footer="true"
      :loading="$apollo.queries.getShootingProgramTemplateSummary.loading"
      disable-pagination
      show-expand
      single-expand
      item-key="id"
      @click:row="refetchShootingProgramTemplateReleases()"
    >
      <template v-slot:item.released="{ item }">
        <v-icon v-if="item.released == 'RELEASED'">mdi-check</v-icon>
        <v-icon v-else>mdi-close</v-icon>
      </template>
      <template v-slot:expanded-item="{ headers }">
        <td :colspan="headers.length">
          <v-card-actions>
            <v-btn
              @click="
                increaseVersion = 'RELEASE';
                generateShootingProgramRelease();
              "
              color="secondary"
              >Generate Release</v-btn
            >
            <v-btn
              @click="changePathEditShootingProgramTemplate()"
              color="secondary"
              >Edit</v-btn
            >
          </v-card-actions>
          <v-text-field
            v-model="inputVersionFilter"
            label="Version Filter"
            name="inputVersionFilter"
            type="text"
            v-on:keyup.enter="submitVersionFilter"
          ></v-text-field>

          <v-data-table
            :headers="subHeaders"
            :items="getShootingProgramTemplateReleaseByFilter"
            item-key="id"
          >
            <!-- ACTIONS -->
            <template v-slot:item.actions="{ item }">
              <!-- CHANGE PUBLICATION STATUS BUTTON -->
              <v-btn
                icon
                @click="
                  openPublicationStatusChangeDialog(
                    item.id,
                    item.publicationStatus,
                  )
                "
              >
                <v-icon>mdi-earth</v-icon>
              </v-btn>

              <!-- DOWNLOAD BUTTON -->
              <v-btn
                icon
                @click="downloadShootingProgramRelease(item.binaryFileUuid)"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>

              <!-- DELETE BUTTON -->
              <v-btn
                icon
                @click="deleteShootingProgramTemplateRelease(item.id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </td>
      </template>
      <!-- ACTIONS -->
      <template v-slot:item.actions="{ item }">
        <!-- DELETE and COPY Template BUTTON-->
        <div id="editTemplateRow" class="row">
          <div class="col-md-3 col-md-3 col-lg-6 d-none d-md-block"></div>
          <div class="col-sm-2 col-md-4 col-lg-3">
            <v-btn icon @click="copyShootingProgramTemplate(item.id)">
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </div>
          <div class="col-sm-2 col-md-4 col-lg-3">
            <v-btn icon @click="deleteShootingProgramTemplate(item.id)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
    </v-data-table>
    <c-confirmation-dialog ref="confirmationDialog"></c-confirmation-dialog>
    <v-dialog v-model="showPublicationStatusChangeDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <h3 class="publication-status-title">Change Publication Status</h3>
        </v-card-title>
        <v-card-text class="publication-status-container">
          <v-radio-group v-model="publicationStatus">
            <v-radio
              v-for="item in publicationStatusOptions"
              :key="item.value"
              :label="item.name"
              :value="item.value"
            ></v-radio>
          </v-radio-group>
          <v-btn @click="changePublicationStatus">Change</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import {
  GET_SHOOTING_PROGRAM_TEMPLATE_RELEASE_FILTER_QUERY,
  GENERATE_SHOOTING_PROGRAM_TEMPLATE_RELEASE,
  DELETE_SHOOTING_PROGRAM_TEMPLATE_RELEASE,
  DELETE_SHOOTING_PROGRAM_TEMPLATE,
  GET_SHOOTING_PROGRAM_TEMPLATE_BASIC_INFO_QUERY,
  COPY_SHOOTING_PROGRAM_TEMPLATE,
  UPDATE_SHOOTING_PROGRAM_TEMPLATE_RELEASE_PUBLICATION_STATUS,
} from '../../graphql/shootingProgramQueries.js';
import { DownloadFileFromBinaryStorage } from '../../utils/UBinaryStorage';

export default {
  components: {
    CConfirmationDialog: () => import('../common/CConfirmationDialog'),
  },
  created() {
    if (this.$session.get('shootingProgramTemplateSearch')) {
      this.search = this.$session.get('shootingProgramTemplateSearch');
    }
    this.refetchShootingProgramTemplates();
  },
  watch: {
    search(value) {
      this.$session.set('shootingProgramTemplateSearch', value);
    },
  },
  data() {
    return {
      id: '',
      search: '',
      increaseVersion: '',
      versionFilter: '',
      inputVersionFilter: '',
      expanded: [],
      subExpanded: [],
      publicationStatus: null, // The publication status to update the release to
      publicationStatusChangeReleaseId: null, // The template release to change the publication status of
      showPublicationStatusChangeDialog: false,
      publicationStatusOptions: [
        {
          name: 'Internal',
          value: 'INTERNAL',
        },
        {
          name: 'Public',
          value: 'PUBLIC',
        },
      ],
      headers: [
        { text: 'Name', value: 'name.value', align: 'left', sortable: true },
        { text: 'Version', value: 'version', sortable: false },
        { text: 'Released', value: 'released', sortable: true },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],
      subHeaders: [
        {
          text: 'Major',
          value: 'versionMajor',
          sortable: false,
          width: '40px',
        },
        {
          text: 'Minor',
          value: 'versionMinor',
          sortable: false,
          width: '40px',
        },
        {
          text: 'Patch',
          value: 'versionPatch',
          sortable: false,
          width: '40px',
        },
        { text: 'Date', value: 'dateTime', sortable: false },
        { text: 'Publication', value: 'publicationStatus', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'right',
          width: '18%',
        },
      ],
      getShootingProgramTemplateSummary: [],
      getShootingProgramTemplateReleaseByFilter: [],
    };
  },
  apollo: {
    $client: 'shootingProgramClient',
    getShootingProgramTemplateSummary: {
      query: GET_SHOOTING_PROGRAM_TEMPLATE_BASIC_INFO_QUERY,
      loadingKey: 'loading',
    },
    getShootingProgramTemplateReleaseByFilter: {
      query: GET_SHOOTING_PROGRAM_TEMPLATE_RELEASE_FILTER_QUERY,
      variables() {
        // Reactive parameters
        return {
          id: this.expanded[0].id, // Template ID
          filter: this.versionFilter,
          limit: 5,
        };
      },
      skip() {
        return this.expanded.length === 0; // Skip query when not expanded
      },
      loadingKey: 'loading',
    },
  },
  methods: {
    async deleteShootingProgramTemplate(id) {
      if (
        await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this shooting program template?',
        )
      ) {
        try {
          const templateReleases = await this.$apollo
            .query({
              query: GET_SHOOTING_PROGRAM_TEMPLATE_RELEASE_FILTER_QUERY,
              variables: {
                id: id,
                filter: this.versionFilter,
                limit: 100,
              },
            })
            .then((response) => {
              return response.data; // Query returns an array, we take first element since we use specific ID
            })
            .catch((error) => {
              throw error;
            });

          templateReleases.getShootingProgramTemplateReleaseByFilter.forEach(
            (temp) => {
              this.deleteReleaseHelper(temp.id);
            },
          );

          await this.$apollo.mutate({
            mutation: DELETE_SHOOTING_PROGRAM_TEMPLATE,
            variables: {
              id: id,
            },
          });
          this.refetchShootingProgramTemplates();
          this.refetchShootingProgramTemplateReleases();
        } catch (error) {
          alert('Error deleting template! ' + error.message);
        }
      }
    },

    async deleteShootingProgramTemplateRelease(id) {
      if (
        await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this shooting program template release?',
        )
      ) {
        try {
          await this.deleteReleaseHelper(id);
        } catch (err) {
          alert('Error deleting template! ' + err.message);
        }
        this.refetchShootingProgramTemplateReleases();
      }
    },

    async deleteReleaseHelper(id) {
      await this.$apollo.mutate({
        mutation: DELETE_SHOOTING_PROGRAM_TEMPLATE_RELEASE,
        variables: {
          id: id,
        },
      });
    },

    async copyShootingProgramTemplate(id) {
      if (
        await this.$refs.confirmationDialog.open(
          'Would you like to copy this template and add it to your list of shooting program templates?',
        )
      ) {
        let shootingProgramCopy = this.getShootingProgramTemplateSummary.filter(
          (template) => template.id === id,
        );
        const rememberName = shootingProgramCopy[0].name.value;
        const rememberVersion = shootingProgramCopy[0].version;
        // delete shootingProgramCopy[0].version;

        try {
          await this.$apollo.mutate({
            mutation: COPY_SHOOTING_PROGRAM_TEMPLATE,
            variables: {
              id: id,
            },
          });

          shootingProgramCopy[0].name.value = rememberName;
          shootingProgramCopy[0].version = rememberVersion;
          this.refetchShootingProgramTemplates();
        } catch (error) {
          alert('Error copying template! ' + error.message);
        }
      }
    },
    refetchShootingProgramTemplates() {
      this.$apollo.queries.getShootingProgramTemplateSummary.refetch();
    },
    refetchShootingProgramTemplateReleases() {
      this.$apollo.queries.getShootingProgramTemplateReleaseByFilter.refetch();
      this.versionFilter = '';
    },
    changePathAddShootingProgramTemplate() {
      this.$router.push({ path: '/addshootingprogramtemplate' });
    },
    changePathEditShootingProgramTemplate() {
      this.$router.push({
        path: '/editshootingprogramtemplate' + '/' + this.expanded[0].id,
      });
    },
    submitVersionFilter() {
      this.versionFilter = this.inputVersionFilter;
    },
    async generateShootingProgramRelease() {
      try {
        const templateId = this.expanded[0].id;
        await this.$apollo.mutate({
          mutation: GENERATE_SHOOTING_PROGRAM_TEMPLATE_RELEASE,
          variables: {
            shootingProgramTemplateId: templateId,
            increaseVersion: this.increaseVersion,
          },
        });

        this.increaseVersion = '';
        this.refetchShootingProgramTemplates();
        this.refetchShootingProgramTemplateReleases();
      } catch (error) {
        alert(
          'The service was not able to generate shooting program template release.',
        );
        this.increaseVersion = '';
      }
    },
    async downloadShootingProgramRelease(binaryFileId) {
      DownloadFileFromBinaryStorage(binaryFileId);
    },
    openPublicationStatusChangeDialog(
      shootingProgramTemplateReleaseId,
      currentPublicationStatus,
    ) {
      this.publicationStatus = currentPublicationStatus;
      this.publicationStatusChangeReleaseId = shootingProgramTemplateReleaseId;
      this.showPublicationStatusChangeDialog = true;
    },
    async changePublicationStatus() {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_SHOOTING_PROGRAM_TEMPLATE_RELEASE_PUBLICATION_STATUS,
          variables: {
            id: this.publicationStatusChangeReleaseId,
            publicationStatus: this.publicationStatus,
          },
        });

        this.showPublicationStatusChangeDialog = false;
        this.publicationStatusChangeReleaseId = null;
      } catch (error) {
        alert('Error changing publication status! ' + error.message);
      }
      this.refetchShootingProgramTemplateReleases();
    },
  },
};
</script>

<style scoped>
@media only screen and (max-width: 1000px) {
  #editTemplateRow {
    width: 10%;
  }
}

.publication-status-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.publication-status-title {
  width: 100%;
  text-align: center;
}
</style>
